'use client';

import { ChevronIcon, CloseIcon } from '@insta-club/components';
import { cn } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import Image from 'next/image';
import { FC, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import LoadingIcon from 'src/libs/components/icons/loading';

interface Option {
    value: string | number;
    label: string;
    image?: string;
    description?: string;
}

const containerVariants = cva(
    'relative w-full rounded-lg border bg-white px-3 transition-all duration-200 text-sm font-medium',
    {
        variants: {
            variant: {
                default: 'border-gray-300 focus-within:border-primary-600',
                error: 'border-danger-600 focus-within:border-danger-600',
                active: 'border-primary-600 focus-within:border-primary-600'
            },
            size: {
                default: 'min-h-14 py-2',
                sm: 'min-h-12 py-1.5',
                lg: 'min-h-16 py-2.5'
            }
        },
        defaultVariants: {
            variant: 'default',
            size: 'default'
        }
    }
);

const floatingLabelVariants = cva(
    'absolute right-3 -top-2 text-xs text-gray-400 transition-all duration-200 pointer-events-none bg-white px-1',
    {
        variants: {
            variant: {
                default: 'peer-focus:text-primary-600',
                error: 'text-danger-600 peer-focus:text-danger-600',
                active: 'text-primary-600 peer-focus:text-primary-600'
            }
        },
        defaultVariants: {
            variant: 'default'
        }
    }
);

const badgeVariants = cva(
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-all duration-200 cursor-pointer hover:shadow-sm',
    {
        variants: {
            selected: {
                true: 'bg-primary-600 text-white border-primary-700',
                false: 'bg-primary-100 text-primary-700 border-primary-200 hover:bg-primary-200'
            }
        },
        defaultVariants: {
            selected: false
        }
    }
);

const selectedBadgeVariants = cva(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium bg-primary-600 text-white border-primary-700'
);

export interface BadgeSelectMultiProps extends VariantProps<typeof containerVariants> {
    name: string;
    label?: string;
    options: Option[];
    placeholder?: string;
    disabled?: boolean;
    isSearchable?: boolean;
    isLoading?: boolean;
    maxLength?: number;
    onChange?: (items: Option[]) => void;
    startAdornment?: React.ReactElement;
    endAdornment?: React.ReactElement;
}

const BadgeSelectMulti: FC<BadgeSelectMultiProps> = ({
    name,
    label,
    options,
    placeholder = 'انتخاب کنید',
    disabled,
    isSearchable,
    variant,
    size,
    isLoading,
    maxLength = 20,
    onChange,
    startAdornment,
    endAdornment
}) => {
    const [showMenu, setShowMenu] = useState(false);
    const [selectedValues, setSelectedValues] = useState<Option[]>([]);
    const [searchValue, setSearchValue] = useState('');
    const inputRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const { register, setValue, watch, formState } = useFormContext();

    const fieldValue = watch(name);
    const hasError = formState.errors[name];
    const currentVariant = hasError ? 'error' : variant;

    useEffect(() => {
        const handler = (event: MouseEvent) => {
            if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };
        window.addEventListener('click', handler);
        return () => window.removeEventListener('click', handler);
    }, []);

    useEffect(() => {
        if (fieldValue === undefined) {
            setValue(name, []);
        }
    }, [name, setValue, fieldValue]);

    useEffect(() => {
        if (Array.isArray(fieldValue)) {
            const selected = options.filter((opt) => fieldValue.includes(opt.value));
            setSelectedValues(selected);
        } else {
            setSelectedValues([]);
        }
    }, [fieldValue, options]);

    const handleToggle = (option: Option) => {
        const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
        const isSelected = currentValues.includes(option.value);

        const newValues = isSelected
            ? currentValues.filter((v) => v !== option.value)
            : [...currentValues, option.value];

        setValue(name, newValues);

        const selectedOptions = options.filter((opt) => newValues.includes(opt.value));
        onChange?.(selectedOptions);
    };

    const handleRemove = (e: React.MouseEvent, optionValue: string | number) => {
        e.stopPropagation();
        const currentValues = Array.isArray(fieldValue) ? fieldValue : [];
        const newValues = currentValues.filter((v) => v !== optionValue);
        setValue(name, newValues);

        const selectedOptions = options.filter((opt) => newValues.includes(opt.value));
        onChange?.(selectedOptions);
    };

    const getFilteredOptions = () => {
        if (!searchValue) return options;
        return options.filter((o) => o.label.toLowerCase().includes(searchValue.toLowerCase()));
    };

    const truncateText = (text: string, max: number): string => {
        if (text.length <= max) return text;
        return text.slice(0, max) + '...';
    };

    return (
        <div className='flex w-full flex-col gap-1'>
            <div
                ref={inputRef}
                className={cn(
                    containerVariants({ variant: showMenu ? 'active' : currentVariant, size }),
                    disabled && 'cursor-not-allowed bg-gray-100 opacity-60'
                )}
            >
                <div
                    className='flex w-full cursor-pointer items-center gap-2'
                    onClick={() => !disabled && !isLoading && setShowMenu((p) => !p)}
                >
                    {startAdornment && <div className='flex items-center'>{startAdornment}</div>}

                    <div className='peer min-w-0 flex-1'>
                        {selectedValues.length > 0 ? (
                            <div className='flex flex-wrap gap-1.5'>
                                {selectedValues.map((option) => (
                                    <div
                                        key={option.value}
                                        className={cn(selectedBadgeVariants())}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {option.image && (
                                            <Image
                                                src={option.image}
                                                alt={option.label}
                                                width={14}
                                                height={14}
                                                className='rounded-sm'
                                            />
                                        )}
                                        <span>{truncateText(option.label, maxLength)}</span>
                                        <button
                                            type='button'
                                            onClick={(e) => handleRemove(e, option.value)}
                                            className='hover:bg-primary-700 rounded-full p-0.5 transition-colors'
                                        >
                                            <CloseIcon width={10} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <span className='text-sm text-gray-400'>{placeholder}</span>
                        )}
                    </div>

                    {endAdornment && <div className='flex items-center'>{endAdornment}</div>}

                    {isLoading && (
                        <div className='flex items-center justify-center'>
                            <LoadingIcon className='text-secondary-500 h-5 w-5 animate-spin' />
                        </div>
                    )}

                    <ChevronIcon
                        width={16}
                        className={clsx('flex-shrink-0 transition-transform', showMenu && '-rotate-90')}
                    />

                    {label && (
                        <label
                            className={cn(
                                floatingLabelVariants({ variant: showMenu ? 'active' : currentVariant }),
                                disabled && 'opacity-70'
                            )}
                        >
                            {label}
                        </label>
                    )}
                </div>

                {showMenu && (
                    <div className='shadow-main bg-primary-50 absolute right-0 z-50 w-full translate-y-0.5 rounded-md border border-gray-200'>
                        {isSearchable && (
                            <div className='relative p-2.5'>
                                <input
                                    ref={searchRef}
                                    type='text'
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    placeholder='جستجو...'
                                    className='focus:border-primary-600 focus:ring-primary-100 w-full rounded-md border border-gray-200 bg-white px-3 py-2 pl-8 text-sm outline-none focus:ring-2'
                                />

                                {searchValue && (
                                    <button
                                        type='button'
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchValue('');
                                            searchRef.current?.focus();
                                        }}
                                        className='absolute top-1/2 left-4 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                                    >
                                        <CloseIcon width={10} />
                                    </button>
                                )}
                            </div>
                        )}
                        <div className='flex max-h-52 flex-wrap gap-2 overflow-auto p-3'>
                            {getFilteredOptions().map((option) => {
                                const selected = Array.isArray(fieldValue) && fieldValue.includes(option.value);

                                return (
                                    <div
                                        key={option.value}
                                        onClick={() => handleToggle(option)}
                                        className={cn(badgeVariants({ selected }))}
                                    >
                                        {option.image && (
                                            <Image
                                                src={option.image}
                                                alt={option.label}
                                                width={16}
                                                height={16}
                                                className='rounded-sm'
                                            />
                                        )}
                                        <span>{option.label}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {hasError && (
                <p className='text-danger-600 text-right text-sm font-medium'>
                    {formState.errors[name]?.message as string}
                </p>
            )}

            <input type='hidden' {...register(name)} />
        </div>
    );
};

export default BadgeSelectMulti;
